import { Row } from '@tanstack/react-table'

import { TrendLabel } from '@/components/customComponents'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utils'

interface TooltipData {
  metricName: string
  metricValue: number
}
interface DataTableColumnCellProps<TData>
  extends React.HTMLAttributes<HTMLDivElement> {
  row: Row<TData>
  accessorKey: string
  format?: Format
  tooltipValues?: TooltipData[]
  prevValue?: number
}

export function DataTableColumnCell<TData>({
  row,
  accessorKey,
  format,
  tooltipValues,
  className,
  prevValue,
}: DataTableColumnCellProps<TData>) {
  const value = row.getValue(accessorKey)

  if (format) {
    const amount = parseFloat(String(value))
    if (Array.isArray(value)) {
      return (
        <div className={cn('flex flex-col justify-end h-full', className)}>
          {[...value].reverse().map((v, i) => (
            <div key={i} className="truncate">
              {formatCell(format, v)}
            </div>
          ))}
        </div>
      )
    }
    if (Number.isNaN(amount) || Number(amount) === 0) {
      return <div className={cn(className)}>-</div>
    }
    const formatted = formatCell(format, amount)

    return (
      <>
        {tooltipValues ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={cn('flex items-center gap-2', className)}>
                  {formatted}
                  {prevValue && (
                    <TrendLabel
                      oldValue={prevValue}
                      newValue={amount}
                      isNormal={true}
                      trendType={undefined as any}
                    />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {tooltipValues.map((value) => (
                  <div key={value.metricName}>
                    {value.metricName}: {value.metricValue}
                  </div>
                ))}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className={cn('flex items-center gap-2', className)}>
            {formatted}
            {prevValue && (
              <TrendLabel
                oldValue={prevValue}
                newValue={amount}
                isNormal={true}
                trendType={undefined as any}
              />
            )}
          </div>
        )}
      </>
    )
  }

  return <div className={cn(className)}>{row.getValue(accessorKey)}</div>
}

function formatCell(format: Format, amount: number): string {
  let formatted: string = ''

  switch (format) {
    case Format.DOLLAR:
      formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount)
      break
    case Format.EURO:
      formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
      }).format(amount)
      break
    case Format.POUND:
      formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'GBP',
      }).format(amount)
      break
    case Format.PERCENT:
      formatted = `${amount}%`
      break
    case Format.PERCENTAGE_INCREASE:
      formatted = `${amount > 0 ? '+' : ''}${amount}%`
      break
    default:
      formatted = amount.toString()
  }

  return formatted
}

export enum Format {
  DOLLAR = 'USD',
  EURO = 'EUR',
  POUND = 'GBP',
  PERCENT = 'PERCENT',
  PERCENTAGE_INCREASE = 'PERCENTAGE_INCREASE',
  NUMBER = 'NUMBER',
}
